import { useForm } from 'react-hook-form'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import { SystemVersion } from '@magnetar/admin-api-interfaces'
import { systemVersionService } from '@/services/system_version_service'
import { toast } from 'sonner'
import { useAuth } from '@/hooks/useAuth'
import { Uploader } from '@/components/Uploader'

type FormValues = {
  version: string
  min_backend_version: string
  file_source: string
  memo: string | null
  tag: string | null
  is_active: boolean
}

interface SystemVersionEditDialogProps {
  systemVersion?: SystemVersion // If provided, we're in edit mode
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess: () => void // Callback to refresh the data
}

export function SystemVersionEditDialog({
  systemVersion,
  open,
  onOpenChange,
  onSuccess,
}: SystemVersionEditDialogProps) {
  const { token } = useAuth()
  const isEditMode = !!systemVersion

  const form = useForm<FormValues>({
    defaultValues: {
      version: systemVersion?.version || '',
      min_backend_version: systemVersion?.min_backend_version || '',
      file_source: systemVersion?.file_source || '',
      memo: systemVersion?.memo || '',
      tag: systemVersion?.tag || '',
      is_active: systemVersion?.is_active || true,
    },
  })

  const onSubmit = async (values: FormValues) => {
    try {
      const payload = {
        version: values.version,
        min_backend_version: values.min_backend_version,
        file_source: values.file_source,
        memo: values.memo,
        tag: values.tag,
        is_active: values.is_active,
      }

      let result
      if (isEditMode && systemVersion) {
        result = await systemVersionService.updateSystemVersion(
          systemVersion.update_id,
          payload,
          token,
        )
      } else {
        result = await systemVersionService.createSystemVersion(payload, token)
      }

      if (result.success) {
        toast.success(`${isEditMode ? '更新' : '新增'}系統版本成功`)
        form.reset()
        onSuccess()
        onOpenChange(false)
      } else {
        toast.error(`${isEditMode ? '更新' : '新增'}系統版本失敗`)
      }
    } catch {
      toast.error(`${isEditMode ? '更新' : '新增'}系統版本失敗`)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{isEditMode ? '編輯系統版本' : '新增系統版本'}</DialogTitle>
          <DialogDescription>
            {isEditMode ? '修改系統版本的基本資料' : '建立一個新的系統版本'}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="version"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>版本號</FormLabel>
                  <FormControl>
                    <Input placeholder="輸入版本號" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="min_backend_version"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>最低後端版本</FormLabel>
                  <FormControl>
                    <Input placeholder="輸入最低後端版本" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="file_source"
              render={({ field: { onChange, value } }) => (
                <FormItem>
                  <FormLabel>檔案來源</FormLabel>
                  <FormControl>
                    <div className="space-y-2">
                      {value && !value.startsWith('http') && (
                        <div className="text-sm text-muted-foreground">當前檔案：{value}</div>
                      )}
                      <Uploader
                        folder="system-versions"
                        allowedFileTypes={['.apk']}
                        onUploadSuccess={({ uuid }) => {
                          onChange(uuid)
                        }}
                        onDelete={() => {
                          onChange('')
                        }}
                      />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="memo"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>備註</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="輸入備註"
                      className="resize-none"
                      {...field}
                      value={field.value || ''}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="tag"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>分類標籤</FormLabel>
                  <FormControl>
                    <Input placeholder="輸入標籤（可選）" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="is_active"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                  <FormControl>
                    <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>啟用狀態</FormLabel>
                    <p className="text-sm text-muted-foreground">設定此系統版本是否啟用</p>
                  </div>
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                取消
              </Button>
              <Button type="submit">{isEditMode ? '更新' : '建立'}</Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
